import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ArrowLeft, Play, ExternalLink, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DemoPage = () => {
  const [visibleVideos, setVisibleVideos] = useState([]);
  const [demoVideos, setDemoVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Fetch videos on component mount
  useEffect(() => {
    fetchVideos();
  }, []);

  // Animation effect for video cards
  useEffect(() => {
    if (demoVideos.length > 0) {
      setVisibleVideos([]); // Reset animation
      demoVideos.forEach((_, index) => {
        setTimeout(() => {
          setVisibleVideos(prev => [...prev, index]);
        }, index * 300 + 800);
      });
      
      // Set first video as selected if no video is selected
      if (!selectedVideo && demoVideos.length > 0) {
        setSelectedVideo(demoVideos[0]);
      }
    }
  }, [demoVideos, selectedVideo]);

  const fetchVideos = async (forceRefresh = false) => {
    try {
      setError(null);
      if (forceRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Try the HostGator API endpoint first
      let response;
      try {
        response = await axios.get(`/api/youtube`, {
          params: { force_refresh: forceRefresh }
        });
      } catch (apiError) {
        // Fallback to original backend URL if available
        if (BACKEND_URL && BACKEND_URL !== '/api') {
          response = await axios.get(`${API}/youtube/videos`, {
            params: { force_refresh: forceRefresh }
          });
        } else {
          throw apiError;
        }
      }

      // Handle both response formats (old and new)
      let videosData = response.data;
      if (response.data.videos) {
        videosData = response.data.videos;
      }

      // Transform PHP API response to match frontend expectations
      const transformedVideos = videosData.map((video, index) => ({
        id: `video_${index}`, // Use consistent ID format
        title: video.title,
        videoId: video.id, // PHP API returns video.id as the YouTube video ID
        channel: video.channelTitle || 'BTSSOFT',
        description: video.description,
        duration: '10:30', // Default duration
        thumbnail: video.thumbnail,
        publishedAt: video.publishedAt,
        viewCount: Math.floor(Math.random() * 5000) + 500 // Random view count
      }));

      console.log('Transformed videos:', transformedVideos); // Debug log
      
      setDemoVideos(transformedVideos);
      
      if (transformedVideos.length > 0 && !selectedVideo) {
        console.log('Setting selected video:', transformedVideos[0]); // Debug log
        setSelectedVideo(transformedVideos[0]);
      }
      
    } catch (err) {
      console.error('Error fetching videos:', err);
      setError('Gagal memuat video. Menggunakan data demo.');
      
      // Fallback to demo data with proper YouTube IDs
      const fallbackVideos = [
        {
          id: "fallback_1",
          title: "XAI Demo - Tutorial Lengkap Sistem Akuntansi UMKM",
          videoId: "tgbNymZ7vqY",
          channel: "BTSSOFT Official",
          description: "Tutorial lengkap penggunaan XAI - Ximple Accounting System untuk UMKM Indonesia. Pelajari fitur-fitur lengkap dari input transaksi hingga laporan keuangan.",
          duration: "10:30",
          thumbnail: "https://img.youtube.com/vi/tgbNymZ7vqY/mqdefault.jpg",
          publishedAt: new Date().toISOString(),
          viewCount: 1000
        },
        {
          id: "fallback_2",
          title: "Cara Setup XAI untuk Bisnis Retail & Toko",
          videoId: "6stlCkUDG_s",
          channel: "BTSSOFT Tutorial", 
          description: "Panduan step-by-step setup XAI untuk bisnis retail dan toko. Mulai dari konfigurasi awal, input master data, hingga pembuatan laporan penjualan harian.",
          duration: "12:15",
          thumbnail: "https://img.youtube.com/vi/6stlCkUDG_s/mqdefault.jpg",
          publishedAt: new Date().toISOString(),
          viewCount: 800
        },
        {
          id: "fallback_3",
          title: "Integrasi WhatsApp dengan XAI - Notifikasi Otomatis",
          videoId: "Ks-_Mh1QhMc",
          channel: "BTSSOFT Official",
          description: "Pelajari cara mengintegrasikan notifikasi WhatsApp dengan sistem XAI. Dapatkan laporan penjualan, stok, dan transaksi otomatis langsung ke WhatsApp.",
          duration: "8:45",
          thumbnail: "https://img.youtube.com/vi/Ks-_Mh1QhMc/mqdefault.jpg",
          publishedAt: new Date().toISOString(),
          viewCount: 1200
        },
        {
          id: "fallback_4",
          title: "Laporan Keuangan Otomatis dengan XAI",
          videoId: "c0KYU2j0TM4",
          channel: "BTSSOFT Tutorial",
          description: "Buat laporan keuangan lengkap secara otomatis dengan XAI. Neraca, laba rugi, arus kas, dan laporan pajak dalam hitungan detik untuk bisnis UMKM.",
          duration: "15:30",
          thumbnail: "https://img.youtube.com/vi/c0KYU2j0TM4/mqdefault.jpg",
          publishedAt: new Date().toISOString(),
          viewCount: 950
        },
        {
          id: "fallback_5",
          title: "Manajemen Inventory & Stok dengan XAI",
          videoId: "LDU_Txk06tM",
          channel: "BTSSOFT Official",
          description: "Kelola inventory dan stok barang dengan mudah menggunakan XAI. Fitur auto-reorder, tracking stok real-time, dan integrasi dengan sistem penjualan.",
          duration: "11:20",
          thumbnail: "https://img.youtube.com/vi/LDU_Txk06tM/mqdefault.jpg",
          publishedAt: new Date().toISOString(),
          viewCount: 750
        },
        {
          id: "fallback_6",
          title: "XAI Mobile App - Kelola Bisnis dari Smartphone",
          videoId: "y8Yv4pnO7qc",
          channel: "BTSSOFT Tutorial",
          description: "Gunakan XAI Mobile App untuk kelola bisnis dari smartphone. Input transaksi, cek stok, monitoring penjualan kapan saja di mana saja.",
          duration: "9:15",
          thumbnail: "https://img.youtube.com/vi/y8Yv4pnO7qc/mqdefault.jpg",
          publishedAt: new Date().toISOString(),
          viewCount: 680
        },
        {
          id: "fallback_7",
          title: "Multi Cabang & User Management XAI",
          videoId: "BaW_jenozKc",
          channel: "BTSSOFT Official",
          description: "Setup sistem multi cabang dengan XAI. Kelola user access, permission, dan sinkronisasi data antar cabang secara real-time dan aman.",
          duration: "13:40",
          thumbnail: "https://img.youtube.com/vi/BaW_jenozKc/mqdefault.jpg",
          publishedAt: new Date().toISOString(),
          viewCount: 920
        },
        {
          id: "fallback_8",
          title: "Backup & Restore Data XAI - Keamanan Terjamin",
          videoId: "hTWKbfoikeg",
          channel: "BTSSOFT Tutorial",
          description: "Pelajari cara backup dan restore data XAI. Jaminan keamanan data bisnis Anda dengan sistem backup otomatis dan manual yang mudah digunakan.",
          duration: "7:55",
          thumbnail: "https://img.youtube.com/vi/hTWKbfoikeg/mqdefault.jpg",
          publishedAt: new Date().toISOString(),
          viewCount: 1100
        },
        {
          id: "fallback_9",
          title: "Customisasi Dashboard & Report XAI",
          videoId: "ZbZSe6N_BXs",
          channel: "BTSSOFT Official",
          description: "Customisasi dashboard dan report sesuai kebutuhan bisnis Anda. Buat grafik, chart, dan analisis yang membantu decision making bisnis UMKM.",
          duration: "14:25",
          thumbnail: "https://img.youtube.com/vi/ZbZSe6N_BXs/mqdefault.jpg",
          publishedAt: new Date().toISOString(),
          viewCount: 1350
        },
        {
          id: "fallback_10",
          title: "Migrasi dari Sistem Lama ke XAI",
          videoId: "FTQbiNvZqaY",
          channel: "BTSSOFT Tutorial",
          description: "Panduan migrasi dari sistem akuntansi lama ke XAI. Import data, training team, dan tips untuk transisi yang smooth tanpa gangguan operasional.",
          duration: "16:10",
          thumbnail: "https://img.youtube.com/vi/FTQbiNvZqaY/mqdefault.jpg",
          publishedAt: new Date().toISOString(),
          viewCount: 890
        }
      ];
      
      console.log('Using fallback videos:', fallbackVideos); // Debug log
      
      setDemoVideos(fallbackVideos);
      if (!selectedVideo) {
        setSelectedVideo(fallbackVideos[0]);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefreshVideos = () => {
    fetchVideos(true);
  };

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 via-transparent to-purple-600/5"></div>
      <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>

      <div className="relative z-10">
        {/* Header */}
        <header className="bg-black/70 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Link to="/">
                  <Button variant="ghost" className="text-white hover:bg-white/10">
                    <ArrowLeft className="h-5 w-5 mr-2" />
                    Kembali
                  </Button>
                </Link>
                <div>
                  <h1 className="text-2xl font-bold text-white">Demo Video XAI</h1>
                  <p className="text-gray-400">Tutorial dan Demo Lengkap Sistem XAI</p>
                </div>
              </div>
              <div className="text-gray-400">
                <span>Video Demo & Tutorial XAI</span>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Video Player */}
            <div className="lg:col-span-2">
              <Card className="bg-black/60 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/40 overflow-hidden">
                <CardContent className="p-0">
                  {selectedVideo ? (
                    <>
                      <div className="aspect-video relative bg-black">
                        <iframe
                          src={`https://www.youtube.com/embed/${selectedVideo.videoId}?autoplay=0&rel=0&modestbranding=1&showinfo=0&fs=1&cc_load_policy=0&iv_load_policy=3&autohide=0`}
                          title={selectedVideo.title}
                          className="w-full h-full rounded-t-lg"
                          frameBorder="0"
                          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          referrerPolicy="strict-origin-when-cross-origin"
                        ></iframe>
                      </div>
                      <div className="p-6">
                        <h2 className="text-2xl font-bold text-white mb-3">{selectedVideo.title}</h2>
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-4">
                            <span className="bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full text-sm">
                              {selectedVideo.channel}
                            </span>
                            <span className="text-gray-400 text-sm">{selectedVideo.duration}</span>
                          </div>
                          <Button
                            size="sm"
                            className="bg-red-600 hover:bg-red-700 text-white"
                            onClick={() => window.open(`https://www.youtube.com/watch?v=${selectedVideo.videoId}`, '_blank')}
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Tonton di YouTube
                          </Button>
                        </div>
                        <p className="text-gray-300 leading-relaxed">{selectedVideo.description}</p>
                      </div>
                    </>
                  ) : (
                    <div className="aspect-video relative bg-gray-800 flex items-center justify-center">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                        <p className="text-gray-400">Memuat video...</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Playlist Sidebar */}
            <div className="space-y-6">
              {/* Video Playlist */}
              <Card className="bg-black/60 backdrop-blur-xl border border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white text-lg flex items-center">
                        <Play className="h-5 w-5 mr-2" />
                        Video Playlist ({demoVideos.length} video)
                      </CardTitle>
                      <p className="text-gray-400 text-sm mt-2">
                        Video terbaru dari XAI dan BTSSOFT Channel
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRefreshVideos}
                      disabled={refreshing}
                      className="text-gray-400 hover:text-white hover:bg-white/10"
                    >
                      <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                  {error && (
                    <div className="mt-3 p-2 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
                      <p className="text-yellow-300 text-xs">{error}</p>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="space-y-3 max-h-96 overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      <span className="ml-3 text-gray-400">Memuat video...</span>
                    </div>
                  ) : demoVideos.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-400">Tidak ada video tersedia</p>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleRefreshVideos}
                        className="mt-2 text-blue-400 hover:text-blue-300"
                      >
                        Coba lagi
                      </Button>
                    </div>
                  ) : (
                    demoVideos.map((video, index) => {
                      const isVisible = visibleVideos.includes(index);
                      return (
                        <div
                          key={video.id}
                          className={`transition-all duration-700 transform ${
                            isVisible 
                              ? 'translate-x-0 opacity-100' 
                              : 'translate-x-10 opacity-0'
                          }`}
                        >
                          <div
                            onClick={() => setSelectedVideo(video)}
                            className={`p-3 rounded-lg cursor-pointer transition-all duration-300 ${
                              selectedVideo && selectedVideo.id === video.id
                                ? 'bg-blue-600/20 border border-blue-500/30'
                                : 'bg-white/5 hover:bg-white/10 border border-transparent hover:border-white/20'
                            }`}
                          >
                            <div className="flex space-x-3">
                              <div className="relative flex-shrink-0">
                                <img
                                  src={video.thumbnail || `https://img.youtube.com/vi/${video.videoId}/mqdefault.jpg`}
                                  alt={video.title}
                                  className="w-20 h-12 object-cover rounded"
                                />
                                <div className="absolute inset-0 bg-black/40 rounded flex items-center justify-center">
                                  <Play className="h-4 w-4 text-white" />
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-white text-sm font-medium line-clamp-2 mb-1">
                                  {video.title}
                                </h4>
                                <div className="flex items-center justify-between">
                                  <p className="text-gray-400 text-xs">{video.channel}</p>
                                  <span className="text-gray-500 text-xs">{video.duration}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Call to Action */}
          <div className="mt-16 text-center">
            <Card className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-xl border border-white/10 max-w-4xl mx-auto">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-white mb-4">
                  Siap Mencoba XAI untuk Bisnis Anda?
                </h3>
                <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                  Setelah melihat demo-demo di atas, saatnya mengimplementasikan XAI untuk mengoptimalkan 
                  pengelolaan keuangan dan operasional bisnis Anda.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/#contact">
                    <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-3 rounded-full font-semibold">
                      Konsultasi Gratis
                    </Button>
                  </Link>
                  <Button 
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10 px-8 py-3 rounded-full font-semibold"
                    onClick={() => window.open('https://wa.me/6285123456789', '_blank')}
                  >
                    Chat WhatsApp
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoPage;